import axiosInstance from '../../../../fetchConfig/axiosConfig.js';
import { usePersistedDataStore } from '../../../store.js';
import logger from '../../../../utils/logger';
import { isTownViewDevNoPersist } from '../../../../utils/townViewDevMode.js';

/**
 * Generalized service to update case statuses in the backend with optional FE sync.
 * Mirrors the realtime offer update pattern (server-first, then local store update).
 *
 * @param {Object} params
 * @param {string} params.muniCode
 * @param {string|number} params.taxYear
 * @param {string} params.reviewType - e.g., 'Negotiation', 'CaseReview', 'DataCleaning'
 * @param {string[]} params.parcelIds - list of parcel_ids to update
 * @param {Object} params.fieldsToUpdate - fields for DB update (e.g., { assignee: null, DateCompleted: 'YYYY-MM-DD HH:mm:ss' })
 * @param {boolean} params.updateFrontend - if true, reflect changes in negotiation object
 * @param {boolean} [params.townView] - when true in development, skip DB persist (FE only)
 */
export const updateCaseStatusGeneralized = async (params) => {
  const {
    muniCode,
    taxYear,
    reviewType,
    parcelIds,
    fieldsToUpdate,
    updateFrontend = true,
    forceUpdateCompleted = false,
    townView,
    villageRun = 0,
    completeAllCaseTypes = false,
  } = params || {};

  const skipDbPersist = isTownViewDevNoPersist(townView);

  if (!muniCode || !taxYear || !reviewType || !Array.isArray(parcelIds) || parcelIds.length === 0) {
    throw new Error('Missing required parameters for updateCaseStatusGeneralized');
  }

  // 1) Backend update (authoritative)
  let response = { data: { success: true } };
  if (skipDbPersist) {
    logger.log('[TownView dev] Skipping update_tasks_generalized — FE store only');
  } else {
    response = await axiosInstance.post('/update_tasks_generalized', {
      MuniCode: muniCode,
      TaxYear: taxYear,
      reviewType,
      uids: parcelIds,
      updateAllExcept: false,
      fieldsToUpdate,
      forceUpdateCompleted,
      villageRun,
      completeAllCaseTypes,
    });
  }

  // 2) Optionally mirror to FE store
  if (updateFrontend) {
    const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
    const setNegotiationObj = usePersistedDataStore.getState().setNegotiationObj;
    const setNegotiationObjVersion = usePersistedDataStore.getState().setNegotiationObjVersion;

    if (getNegotiationObj && typeof setNegotiationObj === 'function') {
      const updated = { ...getNegotiationObj };

      parcelIds.forEach((pid) => {
        const idx = updated.cases?.findIndex((c) => c.parcel_id === pid);
        if (idx === -1 || idx === undefined) return;

        const originalCase = updated.cases[idx] || {};
        const existingInfo = Array.isArray(originalCase.CaseAssignmentInfo)
          ? [...originalCase.CaseAssignmentInfo]
          : [];

        // Update or add reviewType entry in CaseAssignmentInfo
        const reviewIdx = existingInfo.findIndex((i) => i.ReviewType === reviewType);
        const merged = {
          ReviewType: reviewType,
          ...((reviewIdx !== -1 ? existingInfo[reviewIdx] : {})),
          ...fieldsToUpdate,
        };
        if (reviewIdx !== -1) {
          existingInfo[reviewIdx] = merged;
        } else {
          existingInfo.push(merged);
        }

        updated.cases[idx] = {
          ...originalCase,
          CaseAssignmentInfo: existingInfo,
        };
      });

      setNegotiationObj(updated);
      if (typeof setNegotiationObjVersion === 'function') {
        setNegotiationObjVersion((v) => v + 1);
      }
    }
  }

  return response?.data || { success: true };
};


